/**
 * Redistributes weights across campaign items so they always sum to 100.
 *
 * Rules:
 * - All weights are positive integers summing to 100.
 * - Minimum weight per item is 1.
 * - When one item's weight changes, the delta is distributed proportionally
 *   across the other items.
 * - Rounding remainder is assigned to items with the highest campaign_order
 *   values (last in drag order) until the remainder is consumed.
 */

/** Assign remainder units to items with the highest campaign_order values. */
function applyRemainder(items: CampaignItem[], weights: Map<number, number>, remainder: number): void {
  if (remainder === 0) return;

  const sorted = [...items].sort((a, b) => b.campaign_order - a.campaign_order);
  let remaining = remainder;
  for (const item of sorted) {
    if (remaining === 0) break;
    weights.set(item.id, (weights.get(item.id) ?? 0) + 1);
    remaining--;
  }
}

/**
 * Redistributes weights after one item's weight is edited.
 * Returns a new array with updated weights.
 */
export function redistributeWeights(items: CampaignItem[], editedId: number, newWeight: number): CampaignItem[] {
  if (items.length === 0) return items;
  if (items.length === 1) return [{ ...items[0], weight: 100 }];

  const maxWeight = 100 - (items.length - 1);
  const clampedWeight = Math.max(1, Math.min(newWeight, maxWeight));

  const otherItems = items.filter(item => item.id !== editedId);
  const totalOtherWeight = otherItems.reduce((sum, item) => sum + (item.weight ?? 1), 0);

  const weights = new Map<number, number>();
  weights.set(editedId, clampedWeight);

  const remaining = 100 - clampedWeight;

  if (totalOtherWeight === 0) {
    const baseWeight = Math.floor(remaining / otherItems.length);
    for (const item of otherItems) {
      weights.set(item.id, baseWeight);
    }
    const rem = remaining - baseWeight * otherItems.length;
    applyRemainder(otherItems, weights, rem);
  } else {
    let distributed = 0;
    for (const item of otherItems) {
      const share = Math.max(1, Math.floor(((item.weight ?? 1) / totalOtherWeight) * remaining));
      weights.set(item.id, share);
      distributed += share;
    }
    const rem = remaining - distributed;
    applyRemainder(otherItems, weights, rem);
  }

  return items.map(item => ({ ...item, weight: weights.get(item.id) ?? item.weight }));
}

/**
 * Computes the default weight for a newly added item (10%, min 1)
 * and redistributes the remaining 90% across existing items.
 * Returns the new weight for the incoming item and the updated existing items.
 */
export function computeAddedItemWeights(existingItems: CampaignItem[]): {
  newItemWeight: number;
  updatedItems: CampaignItem[];
} {
  if (existingItems.length === 0) {
    return { newItemWeight: 100, updatedItems: [] };
  }

  const newItemWeight = Math.max(1, Math.floor(100 * 0.1));
  const remaining = 100 - newItemWeight;
  const totalExisting = existingItems.reduce((sum, item) => sum + (item.weight ?? 1), 0);

  const weights = new Map<number, number>();
  let distributed = 0;
  for (const item of existingItems) {
    const share = Math.max(1, Math.floor(((item.weight ?? 1) / totalExisting) * remaining));
    weights.set(item.id, share);
    distributed += share;
  }
  const rem = remaining - distributed;
  applyRemainder(existingItems, weights, rem);

  const updatedItems = existingItems.map(item => ({ ...item, weight: weights.get(item.id) ?? item.weight }));
  return { newItemWeight, updatedItems };
}

/**
 * Removes an item from the list and redistributes its weight proportionally
 * across the remaining items. Returns the updated remaining items.
 */
export function computeRemovedItemWeights(items: CampaignItem[], removedId: number): CampaignItem[] {
  const remaining = items.filter(item => item.id !== removedId);
  if (remaining.length === 0) return [];
  if (remaining.length === 1) return [{ ...remaining[0], weight: 100 }];

  const removedWeight = items.find(item => item.id === removedId)?.weight ?? 0;
  const totalRemaining = remaining.reduce((sum, item) => sum + (item.weight ?? 1), 0);
  const total = totalRemaining + removedWeight;

  const weights = new Map<number, number>();
  let distributed = 0;
  for (const item of remaining) {
    const share = Math.max(1, Math.floor(((item.weight ?? 1) / total) * 100));
    weights.set(item.id, share);
    distributed += share;
  }
  const rem = 100 - distributed;
  applyRemainder(remaining, weights, rem);

  return remaining.map(item => ({ ...item, weight: weights.get(item.id) ?? item.weight }));
}

/**
 * Given existing items and a weight reserved for another item (e.g. a newly added one),
 * proportionally scales the existing items so their weights sum to (100 - takenWeight).
 * Returns a new array with updated weights.
 */
export function redistributeRemainingWeights(existingItems: CampaignItem[], takenWeight: number): CampaignItem[] {
  if (existingItems.length === 0) return [];
  if (existingItems.length === 1) return [{ ...existingItems[0], weight: Math.max(1, 100 - takenWeight) }];

  const remaining = 100 - takenWeight;
  const totalExisting = existingItems.reduce((sum, item) => sum + (item.weight ?? 1), 0);

  const weights = new Map<number, number>();
  let distributed = 0;
  for (const item of existingItems) {
    const share = Math.max(1, Math.floor(((item.weight ?? 1) / totalExisting) * remaining));
    weights.set(item.id, share);
    distributed += share;
  }

  const rem = remaining - distributed;
  applyRemainder(existingItems, weights, rem);

  return existingItems.map(item => ({ ...item, weight: weights.get(item.id) ?? item.weight }));
}

/**
 * Removes the specified items and redistributes their combined weight
 * proportionally across the remaining items.
 * Returns the updated remaining items (removed items are not included).
 */
export function redistributeAfterRemoval(allItems: CampaignItem[], removedIds: number[]): CampaignItem[] {
  const remaining = allItems.filter(item => !removedIds.includes(item.id));
  if (remaining.length === 0) return [];
  if (remaining.length === 1) return [{ ...remaining[0], weight: 100 }];

  const total = remaining.reduce((sum, item) => sum + (item.weight ?? 1), 0);

  const weights = new Map<number, number>();
  let distributed = 0;
  for (const item of remaining) {
    const share = Math.max(1, Math.floor(((item.weight ?? 1) / total) * 100));
    weights.set(item.id, share);
    distributed += share;
  }

  const rem = 100 - distributed;
  applyRemainder(remaining, weights, rem);

  return remaining.map(item => ({ ...item, weight: weights.get(item.id) ?? item.weight }));
}

/**
 * Normalizes items loaded from the API so weights always sum to 100.
 * Single item is locked at 100. Zero-sum distributes evenly.
 */
export function normalizeWeightsToSum(items: CampaignItem[]): CampaignItem[] {
  if (items.length === 0) return [];
  if (items.length === 1) return [{ ...items[0], weight: 100 }];

  const sum = items.reduce((s, item) => s + (item.weight ?? 0), 0);
  if (sum === 100 && items.every(item => (item.weight ?? 0) >= 1)) return items;

  const weights = new Map<number, number>();
  let distributed = 0;

  if (sum === 0) {
    const base = Math.floor(100 / items.length);
    for (const item of items) {
      weights.set(item.id, base);
      distributed += base;
    }
  } else {
    for (const item of items) {
      const share = Math.max(1, Math.floor(((item.weight ?? 1) / sum) * 100));
      weights.set(item.id, share);
      distributed += share;
    }
  }

  applyRemainder(items, weights, 100 - distributed);
  return items.map(item => ({ ...item, weight: weights.get(item.id) ?? item.weight }));
}

/**
 * Produces a run-length-encoded interleaved sequence for a set of campaign
 * playlist items using a Bresenham weighted round-robin.
 *
 * Cycle length = max(ceil(totalWeight / weight[i])) — the minimum number of
 * slots required for every entry to appear at least once.
 *
 * Algorithm:
 *   Pre-seed: acc[i] = weight[i]
 *   For each slot:
 *     1. Increment all: acc[i] += weight[i]
 *     2. Sort candidates by acc descending; pick first satisfying min_gap.
 *        Fallback: if all violate min_gap, pick highest acc unconditionally.
 *     3. Decrement winner: acc[winner] -= totalWeight
 *     4. Record winner.lastSlot = slot
 *     5. Emit winner
 *
 * Returns one entry per contiguous run of the same playlist, with:
 *   playlist_name — playlist display name
 *   weight        — the item's weight value
 *   slot          — number of consecutive slots this run occupies in the cycle
 */
export function getInterleavedSequence(
  items: CampaignItem[],
): Array<{ playlist_name: string; weight: number; slot: number }> {
  const weighted = items.filter(item => (item.weight ?? 0) > 0).sort((a, b) => a.campaign_order - b.campaign_order);
  if (weighted.length === 0) return [];

  const totalWeight = weighted.reduce((sum, item) => sum + (item.weight ?? 0), 0);
  if (totalWeight === 0) return [];

  // Minimum slots for every entry to appear at least once
  const cycleLength = Math.max(...weighted.map(item => Math.ceil(totalWeight / (item.weight ?? 0))));

  type State = { idx: number; acc: number; lastSlot: number; minGap: number };
  const states: State[] = weighted.map((item, i) => ({
    idx: i,
    acc: item.weight ?? 0, // pre-seeded
    lastSlot: -Infinity,
    minGap: item.settings?.min_gap ?? 0,
  }));

  const rawSequence: number[] = [];

  for (let slot = 0; slot < cycleLength; slot++) {
    // Step 1: Increment all accumulators
    for (const state of states) {
      state.acc += weighted[state.idx].weight ?? 0;
    }

    // Step 2: Sort by accumulator descending; pick first satisfying min_gap
    const candidates = states.slice().sort((a, b) => b.acc - a.acc);
    const winner = candidates.find(c => slot - c.lastSlot > c.minGap) ?? candidates[0];

    // Step 3: Decrement winner
    winner.acc -= totalWeight;

    // Step 4: Record last selected slot
    winner.lastSlot = slot;

    // Step 5: Emit
    rawSequence.push(winner.idx);
  }

  // Run-length encode into { playlist_name, weight, slot }
  const result: Array<{ playlist_name: string; weight: number; slot: number }> = [];
  for (const idx of rawSequence) {
    const item = weighted[idx];
    const playlistName = item.playlist_name ?? "";
    const last = result[result.length - 1];
    if (last && last.playlist_name === playlistName) {
      last.slot++;
    } else {
      result.push({ playlist_name: playlistName, weight: item.weight ?? 0, slot: 1 });
    }
  }

  return result;
}

/**
 * Produces a time-slice sequence for a set of campaign playlist items.
 *
 * Weights control screen time proportionally. Each entry's loop count is
 * computed as: loops[i] = max(1, round(weight[i] / min(weights)))
 * mirroring the TimeSliceMixer algorithm where all item durations are equal.
 *
 * Items are emitted in campaignOrder; each appears as a single run of
 * slot = loops[i] consecutive slots.
 *
 * Returns one entry per item with:
 *   playlist_name — playlist display name
 *   weight        — the item's weight value
 *   slot          — proportional loop count for this playlist
 */
export function getTimeSliceSequence(
  items: CampaignItem[],
): Array<{ playlist_name: string; weight: number; slot: number }> {
  const weighted = items.filter(item => (item.weight ?? 0) > 0).sort((a, b) => a.campaign_order - b.campaign_order);

  if (weighted.length === 0) return [];

  const minWeight = Math.min(...weighted.map(item => item.weight ?? 0));

  return weighted.map(item => ({
    playlist_name: item.playlist_name ?? "",
    weight: item.weight ?? 0,
    slot: Math.max(1, Math.round((item.weight ?? 0) / minWeight)),
  }));
}

/** Formats mixing_strategy for display. */
export function formatMixingStrategy(strategy: Campaign["mixing_strategy"] | undefined): string {
  if (strategy === "time_slice") return "Time Slice";
  if (strategy === "interleaved") return "Interleaved";
  return "";
}
